/**
 * PA Onboarding - First-time user intro screens
 * 4 swipeable cards with progress dots
 */
import { useState } from 'react';
import { Mic, Mail, Calendar, Sparkles, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const SCREENS = [
  {
    icon: Sparkles,
    color: 'from-[#5048e5] to-[#7c3aed]',
    title: 'Your AI Assistant',
    description: 'Ask questions about your clients, finances, and compliance. Get instant answers from all your data sources.',
  },
  {
    icon: Mic,
    color: 'from-[#ef4444] to-[#f97316]',
    title: 'Voice Powered',
    description: 'Hold the mic button and speak. Schedule meetings, ask about clients, or dictate emails hands-free.',
  },
  {
    icon: Mail,
    color: 'from-[#3b82f6] to-[#06b6d4]',
    title: 'Email Intelligence',
    description: 'Connect Gmail or your company email. AI classifies, summarises, and drafts replies for you.',
  },
  {
    icon: Calendar,
    color: 'from-[#22c55e] to-[#10b981]',
    title: 'Calendar & Deadlines',
    description: 'Track ATO deadlines, sync Google Calendar, and add events with voice commands.',
  },
];

export function Onboarding({ onComplete }: { onComplete: () => void }) {
  const [current, setCurrent] = useState(0);
  const [touchStart, setTouchStart] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = e.changedTouches[0].clientX - touchStart;
    if (diff < -50 && current < SCREENS.length - 1) setCurrent(c => c + 1);
    if (diff > 50 && current > 0) setCurrent(c => c - 1);
  };

  const screen = SCREENS[current];
  const Icon = screen.icon;
  const isLast = current === SCREENS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#0e0a1a] flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Skip button */}
      <div className="flex justify-end p-4">
        <button onClick={onComplete} className="text-xs text-zinc-500 hover:text-zinc-300 px-3 py-1.5 rounded-lg hover:bg-white/[0.04] transition">
          Skip
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 animate-[fadeIn_0.3s_ease-out]" key={current}>
        {/* Icon */}
        <div className={cn('w-28 h-28 rounded-3xl flex items-center justify-center mb-10 bg-gradient-to-br', screen.color)}>
          <Icon className="h-14 w-14 text-white" />
        </div>

        {/* Text */}
        <h2 className="text-2xl font-bold mb-3 text-center">{screen.title}</h2>
        <p className="text-base text-zinc-400 text-center leading-relaxed max-w-xs">{screen.description}</p>
      </div>

      {/* Bottom: dots + button */}
      <div className="px-8 pb-12 flex items-center justify-between">
        {/* Dots */}
        <div className="flex gap-2">
          {SCREENS.map((_, i) => (
            <div
              key={i}
              className={cn('h-2 rounded-full transition-all duration-300', i === current ? 'w-6 bg-[#5048e5]' : 'w-2 bg-zinc-700')}
            />
          ))}
        </div>

        {/* Next/Get Started button */}
        <button
          onClick={() => isLast ? onComplete() : setCurrent(c => c + 1)}
          className={cn(
            'flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition active:scale-95',
            isLast ? 'bg-[#5048e5] text-white' : 'bg-white/[0.06] text-zinc-300 hover:bg-white/[0.1]'
          )}
        >
          {isLast ? 'Get Started' : 'Next'}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
